/**
 * 运行时监控页面
 * 显示所有项目的运行时状态、活跃Session数量、健康度等
 * 基于 agent-runtime-api.md v1.1 协议文档
 */

import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { 
  Activity, AlertCircle, CheckCircle, Clock, RefreshCw, Play, Loader2, 
  ArrowLeft, XCircle, HelpCircle, Workflow
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { 
  runtimeApi, 
  type ProjectRuntimeStatus, 
  type HealthStatus,
  type TestExecuteRequest,
  type TestExecuteResponse 
} from '../services/runtime-api'
import { useToast } from '@/shared/hooks/useToast'

// 健康状态配置
const HEALTH_STATUS_CONFIG: Record<HealthStatus, {
  label: string
  color: string
  bgColor: string
  icon: React.ReactNode
}> = {
  healthy: {
    label: '健康',
    color: 'text-green-600',
    bgColor: 'bg-green-500',
    icon: <CheckCircle className="h-4 w-4" />
  },
  degraded: {
    label: '降级',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-500',
    icon: <AlertCircle className="h-4 w-4" />
  },
  unhealthy: {
    label: '异常',
    color: 'text-red-600',
    bgColor: 'bg-red-500',
    icon: <XCircle className="h-4 w-4" />
  },
  unknown: {
    label: '未知',
    color: 'text-gray-500',
    bgColor: 'bg-gray-400',
    icon: <HelpCircle className="h-4 w-4" />
  }
}

export default function RuntimeMonitoring() {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToast()

  const [projects, setProjects] = useState<ProjectRuntimeStatus[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [testingProjectId, setTestingProjectId] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<TestExecuteResponse | null>(null)
  
  // 测试对话框状态
  const [testDialogOpen, setTestDialogOpen] = useState(false)
  const [testMessage, setTestMessage] = useState('你好')
  const [testDeviceId, setTestDeviceId] = useState('')  // Device ID（可选）
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [selectedProjectName, setSelectedProjectName] = useState<string>('')

  useEffect(() => {
    loadProjects()
    // 每30秒自动刷新
    const interval = setInterval(loadProjects, 30000)
    return () => clearInterval(interval)
  }, [])

  const loadProjects = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await runtimeApi.getProjects()
      console.log('📊 [RuntimeMonitoring] 加载项目列表:', response)
      // 临时兼容：如果后端没有返回 has_published_flow，默认设为 true
      // 因为项目能出现在运行时监控列表中，说明它已经是发布状态
      const projectsWithDefaults = (response.data || []).map(project => ({
        ...project,
        has_published_flow: project.has_published_flow ?? true,
        last_test_at: project.last_test_at ?? null,
        last_test_status: project.last_test_status ?? null,
      }))
      setProjects(projectsWithDefaults)
    } catch (err: unknown) {
      console.error('Failed to load runtime projects:', err)
      const errorMessage = err instanceof Error ? err.message : '加载运行时监控数据失败'
      setError(errorMessage)
      showError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  // 打开测试对话框
  const handleOpenTestDialog = (projectId: string, projectName: string) => {
    setSelectedProjectId(projectId)
    setSelectedProjectName(projectName)
    setTestMessage('你好') // 重置为默认值
    setTestDeviceId('')    // 重置 Device ID
    setTestDialogOpen(true)
  }

  // 确认执行测试
  const handleConfirmTest = async () => {
    if (!selectedProjectId || !testMessage.trim()) return
    
    setTestDialogOpen(false)
    setTestingProjectId(selectedProjectId)
    setTestResult(null)
    
    try {
      const request: TestExecuteRequest = {
        message: testMessage.trim(),
        test_type: 'manual'
      }
      // 只有在 deviceId 有值时才添加到请求中
      if (testDeviceId.trim()) {
        request.device_id = testDeviceId.trim()
      }
      const result = await runtimeApi.executeTest(selectedProjectId, request)
      
      setTestResult(result)
      
      if (result.status === 'passed') {
        showSuccess(`测试通过，耗时: ${result.duration_ms}ms`)
      } else {
        showError(`测试失败: ${result.error_message || '未知错误'}`)
      }
      
      // 刷新数据
      await loadProjects()
    } catch (err: unknown) {
      console.error('Failed to run test:', err)
      const errorMessage = err instanceof Error ? err.message : '执行测试失败'
      showError(errorMessage)
    } finally {
      setTestingProjectId(null)
      setSelectedProjectId(null)
    }
  }

  const getHealthBadge = (status: HealthStatus) => {
    const config = HEALTH_STATUS_CONFIG[status] || HEALTH_STATUS_CONFIG.unknown
    return (
      <Badge className={`${config.bgColor} gap-1`}>
        {config.icon}
        {config.label}
      </Badge>
    )
  }

  const formatLastTestTime = (dateStr: string | null) => {
    if (!dateStr) return '暂无测试'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor(diff / (1000 * 60))

    if (hours > 24) return `${Math.floor(hours / 24)}天前`
    if (hours > 0) return `${hours}小时前`
    if (minutes > 0) return `${minutes}分钟前`
    return '刚刚'
  }

  if (isLoading && projects.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 顶部导航栏 */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Activity className="h-6 w-6 text-primary" />
              </div>
              <span className="font-bold text-xl tracking-tight">运行时监控</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/tools")}>
              工具管理
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate("/memory")} className="gap-2">
              记忆测试
            </Button>
            <Button variant="outline" size="sm" onClick={loadProjects} className="gap-2" disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight mb-2">项目运行状态</h1>
          <p className="text-muted-foreground">
            监控所有项目的运行时状态，包括活跃会话数、健康状态、测试结果等
          </p>
        </div>

        {error && (
          <Card className="mb-6 border-destructive">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 测试结果提示 */}
        {testResult && (
          <Card className={`mb-6 ${testResult.status === 'passed' ? 'border-green-500' : 'border-red-500'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                {testResult.status === 'passed' ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                测试结果：{testResult.status === 'passed' ? '通过' : '失败'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {/* 耗时统计 */}
              <div className="flex flex-wrap gap-4 p-3 bg-muted/50 rounded-lg">
                {testResult.first_token_ms != null && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <span className="text-muted-foreground">首包耗时:</span>
                    <span className="font-semibold text-blue-600">{testResult.first_token_ms}ms</span>
                    <span className="text-xs text-muted-foreground">(TTFB)</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-muted-foreground">完整耗时:</span>
                  <span className="font-semibold">{testResult.duration_ms}ms</span>
                </div>
              </div>

              {/* 执行节点 */}
              {testResult.nodes_executed && testResult.nodes_executed.length > 0 && (
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Workflow className="h-4 w-4 text-primary" />
                    <strong>执行路径</strong>
                    <Badge variant="secondary" className="text-xs">{testResult.nodes_executed.length} 个节点</Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-1">
                    {testResult.nodes_executed.map((node, index) => (
                      <div key={index} className="flex items-center">
                        <Badge variant="outline" className="font-mono text-xs">
                          {node}
                        </Badge>
                        {index < testResult.nodes_executed.length - 1 && (
                          <span className="mx-1 text-muted-foreground">→</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 输入输出 */}
              <div className="space-y-2">
                <div><strong>输入：</strong>{testResult.input_message}</div>
                <div><strong>输出：</strong>{testResult.output_response || <span className="text-muted-foreground italic">无输出</span>}</div>
              </div>

              {/* 错误信息 */}
              {testResult.error_message && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600">
                  <strong>错误：</strong>{testResult.error_message}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 项目列表 */}
        {projects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">暂无运行时数据</p>
              <p className="text-sm text-muted-foreground/60 mt-1">
                创建并发布项目后，运行时状态将显示在这里
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.project_id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">
                        {project.project_name || project.project_id}
                      </CardTitle>
                      <CardDescription className="truncate">
                        ID: {project.project_id.length > 16 
                          ? `${project.project_id.substring(0, 16)}...` 
                          : project.project_id}
                      </CardDescription>
                    </div>
                    {getHealthBadge(project.health_status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* 活跃会话 */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Activity className="h-4 w-4" />
                      <span>活跃会话</span>
                    </div>
                    <Badge variant="secondary" className="font-semibold">
                      {project.active_sessions}
                    </Badge>
                  </div>

                  {/* 流程状态 */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Workflow className="h-4 w-4" />
                      <span>已发布流程</span>
                    </div>
                    {project.has_published_flow ? (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        是
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-muted-foreground">
                        <XCircle className="h-3 w-3 mr-1" />
                        否
                      </Badge>
                    )}
                  </div>

                  {/* 最近测试 */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>最近测试</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {project.last_test_status && (
                        project.last_test_status === 'passed' ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )
                      )}
                      <span className="text-muted-foreground text-xs">
                        {formatLastTestTime(project.last_test_at)}
                      </span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-3 border-t flex justify-between gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/project/${project.project_id}`)}
                  >
                    查看项目
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => handleOpenTestDialog(project.project_id, project.project_name || project.project_id)}
                    disabled={testingProjectId === project.project_id || !project.has_published_flow}
                    title={!project.has_published_flow ? '请先发布流程配置' : '运行测试'}
                  >
                    {testingProjectId === project.project_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )}
                    运行测试
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* 测试输入对话框 */}
      <AlertDialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-primary" />
              执行测试
            </AlertDialogTitle>
            <AlertDialogDescription>
              为 <span className="font-semibold text-foreground">{selectedProjectName}</span> 输入测试消息
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="test-message">测试消息</Label>
              <Input
                id="test-message"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="输入要发送的测试消息..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && testMessage.trim()) {
                    handleConfirmTest()
                  }
                }}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="test-device-id" className="text-muted-foreground">
                Device ID（可选）
              </Label>
              <Input
                id="test-device-id"
                value={testDeviceId}
                onChange={(e) => setTestDeviceId(e.target.value)}
                placeholder="如：device_12345"
              />
              <p className="text-xs text-muted-foreground">
                💡 用于 IoT 设备控制场景，如跑步机
              </p>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmTest}
              disabled={!testMessage.trim()}
            >
              🚀 执行测试
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
